import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import SkillsInput from '../components/SkillsInput';
import {
  Search, LayoutDashboard, Calendar, MessageSquare, Settings,
  Zap, User, Users, X, Filter, CheckCircle, LogOut, Clock, Linkedin
} from 'lucide-react';

// CALENDAR IMPORTS
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enGbLocale from '@fullcalendar/core/locales/en-gb';

import { INDUSTRY_DOMAINS } from '../constants/domains';

import './DiscoverProfessors.css';
import './StudentDashboard.css';

// StudentSidebar removed as it is now handled by the parent StudentDashboard.

function DiscoverProfessors() {
  const navigate = useNavigate();
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProf, setSelectedProf] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [busySlots, setBusySlots] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profFeedback, setProfFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };

  // Updated state to track selection duration
  const [sessionForm, setSessionForm] = useState({
    title: '', description: '', session_type: 'INDIVIDUAL',
    event_date: '', start_time: '', end_time: '', duration: '', group_size: 10, duration_hours: 1
  });
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const [filters, setFilters] = useState({
    nameSearch: '',
    roleFilter: 'ALL',
    domainSearch: '', domain: [], skills: [], minExperience: 0, availability_mode: [], engagement_type: [], minCredibility: 0
  });

  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const fetchUnreadMessages = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages", err);
    }
  };

  useEffect(() => {
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 60000);
    return () => clearInterval(interval);
  }, []);

  const availableDomains = INDUSTRY_DOMAINS;
  const commonSkills = ["Python", "Machine Learning", "Data Science", "React", "Cybersecurity"];
  const deliveryModes = ["Remote", "In-Person", "Hybrid"];

  useEffect(() => {
    const fetchProfs = async () => {
      setLoading(true);
      try {
        const finalDomains = [...filters.domain];
        // domainSearch is now for UI filtering only, not sent to API as a domain

        const params = new URLSearchParams({
          domain: finalDomains.filter(Boolean).join(','),
          skills: filters.skills.join(','),
          min_experience: filters.minExperience,
          mode: filters.availability_mode.join(','),
          min_credibility: filters.minCredibility,
          engagement_categories: filters.engagement_type.join(','),
          personalized: 'true'  // Enable personalization
        }).toString();

        const res = await api.get(`/professors/discover?${params}`);
        setProfessors(res.data);
      } catch (err) {
        console.error("Discovery error:", err);
      } finally {
        setLoading(false);
      }
    };
    const timeoutId = setTimeout(fetchProfs, 300);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Apply Frontend filters (e.g., Role)
  const filteredProfessors = useMemo(() => {
    return professors.filter(p => {
      if (filters.roleFilter === 'POP' && (p.years_of_experience || 0) < 10) return false;
      if (filters.roleFilter === 'INSTRUCTOR' && (p.years_of_experience || 0) >= 10) return false;
      return true;
    });
  }, [professors, filters.roleFilter]);

  // Separate professors by domain preference
  const preferredDomainProfessors = useMemo(() => {
    return filteredProfessors.filter(p => p.is_preferred_domain);
  }, [filteredProfessors]);

  const otherDomainProfessors = useMemo(() => {
    return filteredProfessors.filter(p => !p.is_preferred_domain);
  }, [filteredProfessors]);

  const fetchProfSchedule = async (profId) => {
    setLoadingCalendar(true);
    try {
      const busyRes = await api.get(`/professor/${profId}/busy-slots`);
      setBusySlots(busyRes.data);

      let availData = [];
      try {
        // STRATEGY 1: Direct Availability Endpoint (Singular)
        const res = await api.get(`/professor/${profId}/availability`);
        availData = res.data;
      } catch (e1) {
        try {
          // STRATEGY 2: Plural Availability Endpoint
          const res = await api.get(`/professors/${profId}/availability`);
          availData = res.data;
        } catch (e2) {
          try {
            // STRATEGY 3: Fetch Full Profile (might contain availability)
            const profileRes = await api.get(`/professors/${profId}`);
            if (profileRes.data && profileRes.data.availability) {
              availData = profileRes.data.availability;
            } else {
              // Try alternate profile endpoint
              const profileRes2 = await api.get(`/professor/${profId}`);
              if (profileRes2.data && profileRes2.data.availability) {
                availData = profileRes2.data.availability;
              }
            }
          } catch (e3) {
            console.warn("All availability strategies failed for prof:", profId);
          }
        }
      }

      // Ensure it's an array
      if (!Array.isArray(availData)) availData = [];
      console.log(`Fetched availability for ${profId}:`, availData);
      setAvailability(availData);

    } catch (err) {
      console.error("Calendar fetch error:", err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleOpenProfile = async (prof) => {
    setSelectedProf(prof);
    setShowProfileModal(true);
    setLoadingFeedback(true);
    try {
      const res = await api.get(`/feedback/expert/${prof.id}`);
      setProfFeedback(res.data?.feedbacks || []);
    } catch (err) {
      console.error("Failed to fetch feedback", err);
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleOpenModal = (prof, e) => {
    if (e) e.stopPropagation();
    setSelectedProf(prof);
    setShowModal(true);

    // Check if availability is already included in the professor object
    if (prof.availability && Array.isArray(prof.availability)) {
      setAvailability(prof.availability);
      // Still fetch busy slots
      api.get(`/professor/${prof.id}/busy-slots`)
        .then(res => setBusySlots(res.data))
        .catch(err => console.error(err));
      setLoadingCalendar(false);
    } else {
      fetchProfSchedule(prof.id);
    }

    // Reset selection on open
    setSessionForm(prev => ({
      ...prev,
      event_date: '',
      start_time: '',
      end_time: '',
      duration: '',
      group_size: prof.min_group_size || 10
    }));
  };

  const handleToggleFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!sessionForm.title || !sessionForm.title.trim()) {
      setModalError('Please enter a Session Topic before submitting.');
      return;
    }
    if (!sessionForm.event_date || !sessionForm.start_time) {
      setModalError('Please select a time slot on the calendar.');
      return;
    }

    const selectedDateTime = new Date(`${sessionForm.event_date}T${sessionForm.start_time}`);
    const now = new Date();
    if (selectedDateTime < now) {
      setModalError('You cannot schedule a session in the past. Please select a future time.');
      return;
    }

    // Calculate per-student price based on session type
    const durationMultiplier = sessionForm.duration_hours || 1;
    const calculatedPrice = sessionForm.session_type === 'GROUP'
      ? (selectedProf.group_session_price || selectedProf.individual_session_price || 0) * durationMultiplier
      : (selectedProf.individual_session_price || 0) * durationMultiplier;

    setSubmitting(true);
    try {
      const requestData = {
        ...sessionForm,
        price: calculatedPrice,
        group_size: sessionForm.session_type === 'GROUP' ? sessionForm.group_size : null,
        duration: sessionForm.duration || '1 Hour'
      };

      await api.post(`/student/request-session/${selectedProf.id}`, requestData);
      showToast(`Proposal sent to Prof. ${selectedProf.full_name.split(' ')[0]}.`, "success");
      setShowModal(false);
      setModalError('');
      setSessionForm({ title: '', description: '', session_type: 'INDIVIDUAL', event_date: '', start_time: '', end_time: '', duration: '', group_size: 10, duration_hours: 1 });
    } catch (err) {
      console.error("Session request error:", err);
      const msg = err.response?.data?.detail || "Submission failed. Please try again.";
      setModalError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // --- MERGE BUSY SLOTS WITH CURRENT DYNAMIC SELECTION ---
  const calendarEvents = useMemo(() => {
    const events = [];

    // 1. Availability Background Events
    // Transform availability data into background events that constrain selection
    availability.forEach(slot => {
      // Helper to map day name to FullCalendar day number
      const getDayNumber = (day) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days.indexOf(day);
      };

      if (slot.day_of_week && !slot.specific_date) {
        // Weekly Recurring
        events.push({
          groupId: 'availableForSession',
          daysOfWeek: [getDayNumber(slot.day_of_week)],
          startTime: slot.start_time,
          endTime: slot.end_time,
          display: 'background',
          color: '#6dea99' // Darker green for available slots
        });
      } else if (slot.specific_date) {
        // Specific Date
        let dateOnly = slot.specific_date;
        if (dateOnly.includes('T')) dateOnly = dateOnly.split('T')[0];

        events.push({
          groupId: 'availableForSession',
          start: `${dateOnly}T${slot.start_time}`,
          end: `${dateOnly}T${slot.end_time}`,
          display: 'background',
          color: '#6dea99'
        });
      }
    });

    // 2. Format the busy slots from the backend to look "Blocked"
    // Keep these as normal 'block' events so they visually block the background
    const formattedBusySlots = busySlots.map(slot => ({
      ...slot,
      title: 'Occupied', // Label for busy time
      display: 'block',   // Standard block event
      backgroundColor: 'var(--border)', // Light grey background
      borderColor: '#cbd5e0',     // Darker grey border
      textColor: '#718096',       // Muted text color
      editable: false,            // Prevent dragging
      overlap: false,             // Prevent other events from overlapping
    }));

    events.push(...formattedBusySlots);

    // 3. Add the student's current selection
    if (sessionForm.event_date && sessionForm.start_time && sessionForm.end_time) {
      events.push({
        id: 'current-selection',
        title: sessionForm.title || 'Your Selection',
        start: `${sessionForm.event_date}T${sessionForm.start_time}`,
        end: `${sessionForm.event_date}T${sessionForm.end_time}`,
        backgroundColor: '#4318ff', // Brand blue
        borderColor: '#2b3674',
        textColor: '#ffffff',
        classNames: ['pulse-selection'] // Add a custom CSS class for animation
      });
    }
    return events;
  }, [busySlots, sessionForm, availability]);

  return (
    <div className="stu-discover-container">
      <header className="stu-top-bar">
        <h1>Expert Discovery</h1>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
          Connect with industry leaders for personalized learning
        </span>
      </header>

      <div className="stu-discover-content-grid stu-padded" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '30px' }}>

        <aside className="stu-filters-sidebar">
          <div className="stu-filter-header">
            <h4><Filter size={16} /> Filters</h4>
            <button onClick={() => setFilters({ nameSearch: '', roleFilter: 'ALL', domainSearch: '', skills: [], domain: [], seniority: [], availability_mode: [], engagement_type: [], minCredibility: 0 })} className="stu-reset-link">Clear All</button>
          </div>

          <div className="stu-filter-group">
            <h5>Role</h5>
            <select 
              value={filters.roleFilter || 'ALL'}
              onChange={(e) => setFilters({ ...filters, roleFilter: e.target.value })}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', outline: 'none', fontFamily: 'inherit', fontSize: '14px', color: 'var(--proto-white)', background: 'var(--engine-black)' }}
            >
              <option value="ALL">All Roles</option>
              <option value="POP">Professor of Practice (10+ yrs)</option>
              <option value="INSTRUCTOR">Instructor (&lt; 10 yrs)</option>
            </select>
          </div>

          <div className="stu-filter-group">
            <h5>Industry Domain</h5>
            <input type="text" placeholder="Search domains..." className="stu-filter-search" value={filters.domainSearch} onChange={(e) => setFilters({ ...filters, domainSearch: e.target.value })} />
            <div className="stu-checkbox-list">
              {availableDomains
                .filter(d => {
                  if (!filters.domainSearch) return true;
                  return d.toLowerCase().includes(filters.domainSearch.toLowerCase());
                })
                .slice(0, filters.domainSearch ? undefined : 5)
                .map(d => (
                  <label key={d} className="stu-checkbox-label">
                    <input type="checkbox" checked={filters.domain.includes(d)} onChange={() => handleToggleFilter('domain', d)} /> {d}
                  </label>
                ))}
            </div>
          </div>

          <div className="stu-filter-group">
            <h5>Expertise</h5>
            <SkillsInput selectedSkills={filters.skills} onSkillsChange={(newSkills) => setFilters({ ...filters, skills: newSkills })} />
            <div className="stu-checkbox-list skill-list-margin">

              {commonSkills.map(s => (
                <label key={s} className="stu-checkbox-label">
                  <input type="checkbox" checked={filters.skills.includes(s)} onChange={() => handleToggleFilter('skills', s)} /> {s}
                </label>
              ))}
            </div>
          </div>



          <div className="stu-filter-group">
            <h5>Min. Years of Experience</h5>
            <div className="credibility-slider-container">
              <input
                type="range"
                min="0"
                max="40"
                value={filters.minExperience}
                onChange={(e) => setFilters({ ...filters, minExperience: parseInt(e.target.value) })}
                className="stu-range-slider"
                style={{ background: `linear-gradient(to right, #4338ca 0%, #4338ca ${(filters.minExperience / 40) * 100}%, var(--border) ${(filters.minExperience / 40) * 100}%, var(--border) 100%)` }}
              />
              <div className="slider-value-label">{filters.minExperience}+ Years</div>
            </div>
          </div>

          <div className="stu-filter-group">
            <h5>Min. Credibility Score</h5>
            <div className="credibility-slider-container">
              <input
                type="range"
                min="0"
                max="100"
                value={filters.minCredibility}
                onChange={(e) => setFilters({ ...filters, minCredibility: parseInt(e.target.value) })}
                className="stu-range-slider"
                style={{ background: `linear-gradient(to right, #10b981 0%, #10b981 ${filters.minCredibility}%, var(--border) ${filters.minCredibility}%, var(--border) 100%)` }}
              />
              <div className="slider-value-label">{filters.minCredibility}+ Score</div>
            </div>
          </div>




        </aside >

        <section className="stu-professors-list">
          {/* Search Bar */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ position: 'relative', maxWidth: '600px' }}>
              <Search size={20} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)' }} />
              <input
                type="text"
                placeholder="Search professors by name..."
                value={filters.nameSearch}
                onChange={(e) => setFilters({ ...filters, nameSearch: e.target.value })}
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 44px',
                  fontSize: '15px',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  fontFamily: 'inherit',
                  background: 'var(--engine-black)',
                  color: 'var(--proto-white)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--cyan)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </div>

          {loading ? (
            <div className="stu-loader-container"><div className="stu-spinner"></div></div>
          ) : (
            <>
              {/* PREFERRED DOMAIN EXPERTS */}
              {preferredDomainProfessors.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <div className="stu-list-header" style={{ background: 'var(--blue-dim)', borderLeft: '4px solid var(--vector-blue)', padding: '16px 20px', borderRadius: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--cyan)' }}>
                        ⭐ Experts in Your Preferred Domains
                      </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        <strong>{preferredDomainProfessors.length}</strong> experts match your interests
                      </p>
                    </div>
                  </div>

                  <div className="stu-prof-grid" style={{ marginTop: '20px' }}>
                    {preferredDomainProfessors
                      .filter(p => {
                        if (!filters.nameSearch) return true;
                        return p.full_name?.toLowerCase().includes(filters.nameSearch.toLowerCase());
                      })
                      .map(p => (
                        <div key={`pref-${p.id}`} className="stu-professor-card" style={{ borderLeft: '4px solid var(--cyan)', background: 'var(--bg-card)', cursor: 'pointer' }} onClick={() => handleOpenProfile(p)}>
                          <div className="stu-prof-avatar">{p.profile_photo_path ? <img src={p.profile_photo_path.startsWith('http') ? p.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${p.profile_photo_path.startsWith('/') ? '' : '/'}${p.profile_photo_path}`} alt={p.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (p.full_name ? p.full_name[0] : 'P')}</div>
                          <div className="stu-prof-info">
                            <div className="stu-prof-name-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <h4 style={{ margin: 0 }}>{p.full_name}</h4>
                              <span style={{
                                  fontSize: '10px', 
                                  fontWeight: 'bold', 
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  padding: '3px 8px', 
                                  borderRadius: '12px', 
                                  background: p.years_of_experience >= 10 ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #2563eb, #3b82f6)', 
                                  color: 'white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '2px'
                              }}>
                                  {p.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                              </span>
                              {p.is_online && <span className="stu-online-badge" style={{ marginLeft: 'auto' }}>Online</span>}
                            </div>
                            <p className="stu-prof-headline">{p.job_title} @ {p.company}</p>
                            <div className="stu-badge-container">
                              <span className="stu-domain-pill" style={{ background: 'var(--blue-dim)', color: 'var(--cyan)', border: '1px solid var(--border)' }}><strong>Domain:</strong> {p.domain || "General"}</span>
                            </div>

                            {p.latest_feedbacks && p.latest_feedbacks.length > 0 && (
                              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {p.latest_feedbacks.map((fb, idx) => (
                                  <div key={idx} style={{ padding: '8px', background: 'var(--bg-soft)', borderRadius: '6px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 'bold', color: 'var(--cyan)' }}>
                                      <span style={{ color: '#fbbf24' }}>{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                                      Latest Feedback {p.latest_feedbacks.length > 1 ? `#${idx + 1}` : ''}
                                    </div>
                                    {fb.comment && (
                                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: '1.4' }}>
                                        "{fb.comment.length > 60 ? fb.comment.substring(0, 60) + '...' : fb.comment}"
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* <div className="stu-tags" style={{ alignItems: 'center', marginTop: '12px' }}>
                              <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-muted)' }}>Skills:</span>
                              {p.skills?.map(skill => <span key={skill} className="stu-tag-pill">{skill}</span>)}
                            </div> */}
                            {p.linkedin_url && (
                              <a href={/^https?:\/\//i.test(p.linkedin_url) ? p.linkedin_url : `https://${p.linkedin_url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0077b5', textDecoration: 'none', marginTop: '8px', fontWeight: '500' }}>
                                <Linkedin size={14} /> View LinkedIn Profile
                              </a>
                            )}

                          </div>
                          <div className="stu-prof-pricing-modern" style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                            <div className="stu-prof-credibility" style={{ padding: '0 0 8px 0', marginBottom: '4px' }}>
                              <span className="stu-cred-score" style={{ fontSize: '2rem' }}>{p.credibility || 92}</span>
                              <span className="stu-cred-label" style={{ fontSize: '10px' }}>Score</span>
                            </div>
                            {(p.individual_session_price !== undefined && p.individual_session_price > 0) ? (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--engine-black)', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
                                    <User size={10} /> 1:1
                                  </div>
                                  <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--proto-white)' }}>
                                    ₹{p.individual_session_price}<span style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '400' }}>/hr</span>
                                  </div>
                                </div>
                                  {p.min_group_size > 1 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-soft)', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--cyan)', fontWeight: '500' }}>
                                        <Users size={10} /> Group (Min {p.min_group_size})
                                      </div>
                                    </div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--proto-white)' }}>
                                      ₹{p.group_session_price || p.individual_session_price}<span style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '400' }}>/student</span>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #dcfce7' }}>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#15803d' }}>View Rates</div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', width: '100%' }}>
                              <button className="stu-btn-secondary small-request" onClick={(e) => { e.stopPropagation(); navigate('/chat', { state: { startChatWith: { id: p.id, name: p.full_name, role: 'pop' } } }) }} style={{ flex: 1, padding: '8px', justifyContent: 'center' }}>
                                <MessageSquare size={16} />
                              </button>
                              <button id="book-session-button" className="stu-btn-primary small-request" onClick={(e) => handleOpenModal(p, e)} style={{ flex: 4, justifyContent: 'center' }}>
                                <Zap size={14} /> Request Session
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* OTHER DOMAIN EXPERTS */}
              {otherDomainProfessors.length > 0 && (
                <div>
                  <div className="stu-list-header" style={{ padding: '16px 20px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--proto-white)' }}>
                        {preferredDomainProfessors.length > 0 ? 'Other Expert Professionals' : 'All Expert Professionals'}
                      </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        <strong>{otherDomainProfessors.length}</strong> experts available • Explore beyond your preferences
                      </p>
                    </div>
                  </div>

                  <div className="stu-prof-grid" style={{ marginTop: '20px' }}>
                    {otherDomainProfessors
                      .filter(p => {
                        if (!filters.nameSearch) return true;
                        return p.full_name?.toLowerCase().includes(filters.nameSearch.toLowerCase());
                      })
                      .map(p => (
                        <div key={`other-${p.id}`} className="stu-professor-card hoverable-card" style={{ cursor: 'pointer' }} onClick={() => handleOpenProfile(p)}>
                          <div className="stu-prof-avatar">{p.profile_photo_path ? <img src={p.profile_photo_path.startsWith('http') ? p.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${p.profile_photo_path.startsWith('/') ? '' : '/'}${p.profile_photo_path}`} alt={p.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (p.full_name ? p.full_name[0] : 'P')}</div>
                          <div className="stu-prof-info">
                            <div className="stu-prof-name-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <h4 style={{ margin: 0 }}>{p.full_name}</h4>
                              <span style={{
                                  fontSize: '10px', 
                                  fontWeight: 'bold', 
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  padding: '3px 8px', 
                                  borderRadius: '12px', 
                                  background: p.years_of_experience >= 10 ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #2563eb, #3b82f6)', 
                                  color: 'white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '2px'
                              }}>
                                  {p.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                              </span>
                              {p.is_online && <span className="stu-online-badge" style={{ marginLeft: 'auto' }}>Online</span>}
                            </div>
                            <p className="stu-prof-headline">{p.job_title} @ {p.company}</p>
                            <div className="stu-badge-container">
                              <span className="stu-domain-pill"><strong>Domain:</strong> {p.domain || "General"}</span>
                              {/* <span className="stu-seniority-pill"><strong>Exp:</strong> {p.years_of_experience} Years</span>

                              {p.highest_education && (
                                <span className="stu-education-pill" style={{ background: '#f0f9ff', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid #bae6fd' }}>
                                  <strong>Education:</strong> {p.highest_education}
                                </span>
                              )} */}

                            </div>

                            {p.latest_feedbacks && p.latest_feedbacks.length > 0 && (
                              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {p.latest_feedbacks.map((fb, idx) => (
                                  <div key={idx} style={{ padding: '8px', background: 'var(--bg-soft)', borderRadius: '6px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 'bold', color: 'var(--cyan)' }}>
                                      <span style={{ color: '#fbbf24' }}>{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                                      Latest Feedback {p.latest_feedbacks.length > 1 ? `#${idx + 1}` : ''}
                                    </div>
                                    {fb.comment && (
                                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: '1.4' }}>
                                        "{fb.comment.length > 60 ? fb.comment.substring(0, 60) + '...' : fb.comment}"
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* <div className="stu-tags" style={{ alignItems: 'center', marginTop: '12px' }}>
                              <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-muted)' }}>Skills:</span>
                              {p.skills?.map(skill => <span key={skill} className="stu-tag-pill">{skill}</span>)}
                            </div> */}
                            {p.linkedin_url && (
                              <a href={/^https?:\/\//i.test(p.linkedin_url) ? p.linkedin_url : `https://${p.linkedin_url}`} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0077b5', textDecoration: 'none', marginTop: '8px', fontWeight: '500' }}>
                                <Linkedin size={14} /> View LinkedIn Profile
                              </a>
                            )}

                          </div>
                          <div className="stu-prof-pricing-modern" style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                            <div className="stu-prof-credibility" style={{ padding: '0 0 8px 0', marginBottom: '4px' }}>
                              <span className="stu-cred-score" style={{ fontSize: '1.25rem' }}>{p.credibility || 92}</span>
                              <span className="stu-cred-label" style={{ fontSize: '10px' }}>Score</span>
                            </div>
                            {(p.individual_session_price !== undefined && p.individual_session_price > 0) ? (
                              <>
                                {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--engine-black)', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500' }}>
                                    <User size={10} /> 1:1
                                  </div>
                                  <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--proto-white)' }}>
                                    ₹{p.individual_session_price}<span style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '400' }}>/hr</span>
                                  </div>
                                </div>
                                {p.min_group_size > 1 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', background: '#eef2ff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #e0e7ff' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#4338ca', fontWeight: '500' }}>
                                        <Users size={10} /> Group (Min {p.min_group_size})
                                      </div>
                                    </div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#4f46e5' }}>
                                      ₹{p.group_session_price || p.individual_session_price}<span style={{ fontSize: '10px', color: '#818cf8', fontWeight: '400' }}>/student</span>
                                    </div>
                                  </div>
                                )} */}
                              </>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #dcfce7' }}>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#15803d' }}>View Rates</div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              <button className="stu-btn-outline" onClick={(e) => { e.stopPropagation(); navigate('/chat', { state: { startChatWith: { id: p.id, name: p.full_name, role: 'pop' } } }) }} style={{ flex: 1, justifyContent: 'center', padding: '10px' }}>
                                <MessageSquare size={16} />
                              </button>
                              <button className="stu-btn-primary small-request" onClick={(e) => handleOpenModal(p, e)} style={{ flex: 3, justifyContent: 'center' }}>
                                <Zap size={14} /> Request
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {professors.length === 0 && (
                <div className="stu-empty-state">
                  <p>No experts found matching your criteria. Try adjusting your filters.</p>
                </div>
              )}
            </>
          )}
        </section>

        {showProfileModal && selectedProf && (
          <div className="stu-modal-overlay" onClick={() => setShowProfileModal(false)}>
            <div className="stu-modal-content large-propose pop-fade-up" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', display: 'flex', flexDirection: 'column' }}>
              <div className="stu-modal-header" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                <div className="modal-title-context" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div className="stu-prof-avatar" style={{ width: '64px', height: '64px', fontSize: '24px', flexShrink: 0 }}>
                    {selectedProf.profile_photo_path ? <img src={selectedProf.profile_photo_path.startsWith('http') ? selectedProf.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${selectedProf.profile_photo_path.startsWith('/') ? '' : '/'}${selectedProf.profile_photo_path}`} alt={selectedProf.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (selectedProf.full_name ? selectedProf.full_name[0] : 'P')}
                  </div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--proto-white)' }}>{selectedProf.full_name}</h2>
                    <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '0.95rem' }}>{selectedProf.job_title} @ {selectedProf.company}</p>
                  </div>
                </div>
                <button className="close-btn-large" onClick={() => setShowProfileModal(false)}><X size={28} /></button>
              </div>

              <div className="large-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px', overflowY: 'auto', maxHeight: '70vh' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', background: 'var(--engine-black)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Domain</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.domain || "General"}</span></div>
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Experience</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.years_of_experience} Years</span></div>
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Credibility</strong> <span style={{ color: '#10b981', fontWeight: '700' }}>{selectedProf.credibility || 92}/100</span></div>
                  {selectedProf.highest_education && (
                    <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Education</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.highest_education}</span></div>
                  )}
                </div>

                {selectedProf.bio && (
                  <div>
                    <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>About</h4>
                    <div style={{ background: 'var(--graphite-dark)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', lineHeight: '1.6', color: 'var(--text-muted)', fontSize: '14.5px', whiteSpace: 'pre-line' }}>
                      {selectedProf.bio}
                    </div>
                  </div>
                )}

                {selectedProf.skills && selectedProf.skills.length > 0 && (
                  <div>
                    <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Expertise & Skills</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedProf.skills.map(skill => (
                        <span key={skill} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--proto-white)', borderRadius: '20px', fontSize: '13px', fontWeight: '500', border: '1px solid var(--border)' }}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Latest Feedback
                    {loadingFeedback && <span className="stu-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>}
                  </h4>
                  {!loadingFeedback && profFeedback.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', background: 'var(--engine-black)', borderRadius: '8px', color: 'var(--text-faint)', fontStyle: 'italic', border: '1px solid var(--border)' }}>
                      No feedback received yet.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {profFeedback.slice(0, 3).map(fb => (
                        <div key={fb.id} style={{ padding: '16px', background: 'var(--graphite-dark)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '14px', color: 'var(--proto-white)' }}>{fb.student_name || "Anonymous"}</strong>
                            <span style={{ display: 'flex', gap: '2px', color: '#fbbf24' }}>
                              {Array.from({ length: 5 }).map((_, i) => (
                                <span key={i} style={{ opacity: i < fb.quality_rating ? 1 : 0.3 }}>★</span>
                              ))}
                            </span>
                          </div>
                          {fb.comments && <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.5', fontStyle: 'italic' }}>"{fb.comments}"</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 'auto', paddingTop: '24px', display: 'flex', gap: '16px', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
                  <button
                    className="stu-btn-outline"
                    onClick={() => setShowProfileModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '1.5px solid var(--border)',
                      background: 'var(--graphite-dark)',
                      color: 'var(--text-muted)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--text-faint)'; e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
                  >
                    Close Summary
                  </button>
                  <button
                    className="stu-btn-primary"
                    onClick={() => { setShowProfileModal(false); setTimeout(() => handleOpenModal(selectedProf), 100); }}
                    style={{
                      padding: '12px 28px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(79, 70, 229, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.25)'; }}
                  >
                    <Zap size={18} /> Request Session
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {showModal && selectedProf && (
          <div className="stu-modal-overlay">
            <div className="stu-modal-content large-propose pop-fade-up">
              <div className="stu-modal-header">
                <div className="modal-title-context">
                  <h2>Propose a Session</h2>
                  <p>Drag to select duration on Prof. {selectedProf.full_name}'s schedule</p>
                </div>
                <button className="close-btn-large" onClick={() => setShowModal(false)}><X size={32} /></button>
              </div>

              <form onSubmit={handleRequestSubmit} className="large-modal-form" style={{ display: 'contents' }}>
                <div className="large-modal-body">
                  <div className="modal-section-grid">
                    <div className="modal-form-left">
                      <div className="prof-mini-card-large">
                        <div className="stu-prof-avatar" style={{ width: '60px', height: '60px' }}>{selectedProf.full_name[0]}</div>
                        <div>
                          <h4>{selectedProf.full_name}</h4>
                          <p>{selectedProf.job_title}</p>
                          {/* Constraints UI */}
                          {(selectedProf.weekly_quota_hours || selectedProf.window_start_date || selectedProf.window_end_date) && (
                            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {selectedProf.weekly_quota_hours && (
                                <div style={{ fontSize: '10px', background: '#ecfdf5', color: '#059669', padding: '2px 6px', borderRadius: '4px', border: '1px solid #a7f3d0', fontWeight: '600' }}>
                                  Capacity: {selectedProf.weekly_quota_hours}hr/week
                                </div>
                              )}
                              {(selectedProf.window_start_date || selectedProf.window_end_date) && (
                                <div style={{ fontSize: '10px', background: '#fdf4ff', color: '#c026d3', padding: '2px 6px', borderRadius: '4px', border: '1px solid #f5d0fe', fontWeight: '600' }}>
                                  Valid: {selectedProf.window_start_date ? new Date(selectedProf.window_start_date).toLocaleDateString() : 'TBD'} - {selectedProf.window_end_date ? new Date(selectedProf.window_end_date).toLocaleDateString() : 'TBD'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="type-selection-area">
                        <label className="section-label" style={{ fontWeight: 700, color: 'var(--cyan)' }}>Session Format</label>
                        <div className="session-type-selector-large" style={{ marginTop: '10px' }}>
                          <button type="button" className={`type-btn-large ${sessionForm.session_type === 'INDIVIDUAL' ? 'active' : ''}`} onClick={() => setSessionForm({ ...sessionForm, session_type: 'INDIVIDUAL' })}>
                            <User size={20} />
                            <div>
                              <strong>Individual</strong>
                              <p>Private 1:1 {selectedProf.individual_session_price ? `(₹${selectedProf.individual_session_price}/student)` : ''}</p>
                            </div>
                          </button>
                          <button type="button" className={`type-btn-large ${sessionForm.session_type === 'GROUP' ? 'active' : ''}`} onClick={() => setSessionForm({ ...sessionForm, session_type: 'GROUP' })} style={{ marginTop: '10px' }}>
                            <Users size={20} />
                            <div>
                              <strong>Group</strong>
                              <p>
                                {selectedProf.min_group_size > 1
                                  ? `Min ${selectedProf.min_group_size} people (₹${selectedProf.group_session_price || selectedProf.individual_session_price || 0}/student)`
                                  : `₹${selectedProf.group_session_price || selectedProf.individual_session_price || 0}/student`}
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* DURATION INPUT FOR BOTH TYPES */}
                      <div className="stu-field-wrapper" style={{ marginTop: '20px' }}>
                        <label style={{ fontWeight: 600, color: 'var(--proto-white)', marginBottom: '8px', display: 'block' }}>
                          Session Duration (Hours)
                        </label>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={sessionForm.duration_hours || 1}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 1;
                            setSessionForm({
                              ...sessionForm,
                              duration_hours: val,
                              duration: `${val} Hour${val !== 1 ? 's' : ''}`
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: 'var(--proto-white)',
                            background: 'var(--engine-black)'
                          }}
                        />
                        <p style={{ fontSize: '11px', color: '#a3aed0', marginTop: '4px' }}>
                          Adjusting duration will update the total cost below.
                        </p>
                      </div>

                      {/* Group Size Selector - Only show for GROUP sessions */}
                      {sessionForm.session_type === 'GROUP' && (
                        <div className="stu-field-wrapper" style={{ marginTop: '20px' }}>
                          <label style={{ fontWeight: 600, color: 'var(--proto-white)', marginBottom: '8px', display: 'block' }}>
                            Number of Group Members
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <input
                              type="number"
                              min={selectedProf.min_group_size || 1}
                              value={sessionForm.group_size || selectedProf.min_group_size || 10}
                              onChange={(e) => setSessionForm({
                                ...sessionForm,
                                group_size: parseInt(e.target.value) || selectedProf.min_group_size || 10
                              })}
                              style={{
                                width: '100px',
                                padding: '10px',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'var(--proto-white)',
                                background: 'var(--engine-black)'
                              }}
                            />
                            <div style={{
                              flex: 1,
                              padding: '12px 16px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '10px',
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '11px', opacity: 0.8 }}>Per Student</span>
                                <span style={{ fontSize: '13px', opacity: 0.9 }}>({sessionForm.duration_hours || 1}h session)</span>
                              </div>
                              <span style={{ fontSize: '20px', fontWeight: '700' }}>
                                ₹{(selectedProf.group_session_price || selectedProf.individual_session_price || 0) * (sessionForm.duration_hours || 1)}
                              </span>
                            </div>
                          </div>
                          <p style={{
                            fontSize: '11px',
                            color: '#a3aed0',
                            marginTop: '6px',
                            fontStyle: 'italic'
                          }}>
                            Minimum {selectedProf.min_group_size || 10} members. Rate: ₹{selectedProf.group_session_price || selectedProf.individual_session_price || 0} /student.
                          </p>
                        </div>
                      )}

                      {/* INDIVIDUAL TOTAL DISPLAY */}
                      {sessionForm.session_type === 'INDIVIDUAL' && (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: 'var(--engine-black)',
                          borderRadius: '10px',
                          border: '1px solid var(--border)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600' }}>Total Estimation</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>{sessionForm.duration_hours || 1} Hour(s) @ ₹{selectedProf.individual_session_price}/hr</span>
                          </div>
                          <span style={{ fontSize: '22px', fontWeight: '700', color: 'var(--proto-white)' }}>
                            ₹{(selectedProf.individual_session_price || 0) * (sessionForm.duration_hours || 1)}
                          </span>
                        </div>
                      )}

                      <div className="stu-field-wrapper" style={{ marginTop: '25px' }}>
                        <label>Session Topic</label>
                        <input required placeholder="e.g. Fintech Logic" value={sessionForm.title} onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })} />
                      </div>

                      <div className="stu-field-wrapper" style={{ marginTop: '15px' }}>
                        <label>Learning Objectives</label>
                        <textarea rows="4" placeholder="What do you want to learn?" value={sessionForm.description} onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })} />
                      </div>
                    </div>

                    <div className="modal-form-right">
                      <div className="visual-calendar-picker-container">
                        <label className="section-label" style={{ fontWeight: 700, color: 'var(--cyan)' }}>Click or Drag to Select Time</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', marginBottom: '8px', fontSize: '12px', color: 'var(--green)' }}>
                          <span style={{ width: '10px', height: '10px', backgroundColor: 'var(--green-dim)', border: '1px solid var(--green)', borderRadius: '2px' }}></span>
                          <span>Available slots are highlighted in green</span>
                        </div>
                        {loadingCalendar ? (
                          <div className="calendar-mini-loader">Syncing schedule...</div>
                        ) : (
                          <div className="calendar-wrapper-inner" style={{ marginTop: '10px' }}>
                            {availability.length === 0 && (
                              <div style={{ padding: '8px', marginBottom: '8px', backgroundColor: '#fff7ed', border: '1px solid #ffedd5', borderRadius: '6px', fontSize: '13px', color: '#c2410c' }}>
                                <strong>Note:</strong> Professor (ID: {selectedProf?.id || selectedProf?.user_id || selectedProf?.pop_id}) has not set specific availability. You may request any time slot.
                              </div>
                            )}
                            <FullCalendar
                              plugins={[timeGridPlugin, interactionPlugin]}
                              initialView="timeGridWeek"
                              locale={enGbLocale}
                              headerToolbar={{ left: 'prev,next', center: 'title', right: '' }}
                              allDaySlot={false}
                              height="500px"
                              slotMinTime="08:00:00"
                              slotMaxTime="21:00:00"
                              selectable={true}
                              // Allow overlap overlap ONLY if it is a background event (availability)
                              // Block event (Occupied) will fail this check
                              selectOverlap={(event) => event.display === 'background'}
                              // Only constrain if we actually have availability data
                              selectConstraint={availability.length > 0 ? "availableForSession" : undefined}
                              eventOverlap={false}
                              unselectAuto={false} // Prevents clearing selection when clicking form fields
                              selectAllow={(selectInfo) => {
                                return selectInfo.start >= new Date();
                              }}
                              events={calendarEvents}
                              select={(info) => {
                                const diffMs = info.end - info.start;
                                const diffHrs = diffMs / (1000 * 60 * 60);
                                const intendedDuration = sessionForm.duration_hours || 1;

                                // Auto-snap logic: If selection is default 30min (click) but intended is different, force selection
                                if (diffHrs === 0.5 && intendedDuration !== 0.5) {
                                  const newEnd = new Date(info.start.getTime() + intendedDuration * 60 * 60 * 1000);
                                  // Use setTimeout to avoid recursive blocking within the render cycle
                                  setTimeout(() => {
                                    info.view.calendar.select({ start: info.start, end: newEnd });
                                  }, 10);
                                  return;
                                }

                                // Standard update
                                const durationStr = `${diffHrs} Hour${diffHrs !== 1 ? 's' : ''}`;
                                setSessionForm({
                                  ...sessionForm,
                                  event_date: info.startStr.split('T')[0],
                                  start_time: info.start.toTimeString().substring(0, 5),
                                  end_time: info.end.toTimeString().substring(0, 5),
                                  duration: durationStr,
                                  duration_hours: diffHrs
                                });
                              }}
                              eventClick={(info) => {
                                // If student clicks their own selection, clear it
                                if (info.event.id === 'current-selection') {
                                  setSessionForm({ ...sessionForm, event_date: '', start_time: '', end_time: '', duration: '' });
                                  info.view.calendar.unselect();
                                }
                              }}
                            />
                          </div>
                        )}
                        {sessionForm.event_date && (
                          <div className="slot-selected-confirmation dynamic-selection">
                            <div className="pill-content">
                              <CheckCircle size={14} />
                              <span>{sessionForm.event_date} | {sessionForm.start_time} - {sessionForm.end_time} <strong>({sessionForm.duration})</strong></span>
                            </div>
                            <button type="button" className="clear-selection-btn" onClick={() => setSessionForm({ ...sessionForm, event_date: '', start_time: '', end_time: '', duration: '' })}>
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {modalError && (
                  <div style={{
                    margin: '0 24px 0 24px',
                    padding: '12px 16px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#dc2626',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px' }}>⚠️</span>
                    {modalError}
                  </div>
                )}
                <div className="stu-modal-footer">
                  <button type="button" className="stu-btn-cancel" onClick={() => { setShowModal(false); setModalError(''); }}>Cancel</button>
                  <button
                    type="submit"
                    className="stu-btn-primary-large"
                    disabled={submitting}
                    style={{ opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Proposal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
        }
      </div>
    </div>
  );
}

export default DiscoverProfessors;
